import type { CellDomSchema } from './cell.types'

import {
  useCallback,
  useEffect,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'

import { cn, tv } from 'tailwind-variants'

import {
  useCellDerivedState,
  useCellState,
  useCellStateActions,
} from './CellContext'

import { COLUMN_DEFAULT_WIDTH } from '../column/column.const'

import { useGridSnap } from '../grid/grid.store'
import { useCached } from '@/utils/react/state'

const frameStyle = tv({
  base: [
    'relative', // Position inner toolbar
    'rounded-sm',
    'w-(--col-width) min-w-(--col-width)', // Column width variable set in CellFrame
    'bg-(--cue-cell-bg-color)', // Background color inherited from cue context
    'outline-2 outline-transparent', // base outline (invisible)
    'focus:outline-neutral-200', // focus outline
  ],
  variants: {
    isSelected: {
      true: [
        'outline-neutral-500', // selection outline
      ],
      false: [
        'hover:outline-neutral-500', // hover outline
      ],
    },
    isEditing: {
      true: [
        // Editor handles editing outline
        'bg-(--cue-cell-bg-color)/80', // Override bg with inherited bg color
      ],
    },
    isInPrivateNotesColumn: {
      true: 'bg-(--cue-cell-bg-color)/75', // CueCell color inherited from CueItem
    },
  },
})

export function CellFrame({ children }: ComponentProps<'div'>) {
  const { cellKey, col } = useCellState()

  const { isSelected, isEditing, isInPrivateNotesColumn, isReadOnly } =
    useCellDerivedState()

  const { setIsSelected, setIsEditing } = useCellStateActions()

  // #region ============== EVENTS

  const onClick = () => {
    if (isEditing) return
    if (isSelected) return

    setIsSelected(true)
  }

  const onDoubleClick = (e: MouseEvent) => {
    e.preventDefault()
    if (isEditing) return
    if (isReadOnly) return

    // Enable editing and pass event for focus feature
    setIsEditing(true, e)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    // Only trigger if target is frame, not children
    if (e.target !== e.currentTarget) return

    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault() // TODO: Temp. focus trap to avoid getting stuck in broken cell title editable input
      return
    }
    if (
      e.key === 'Enter' &&
      !e.ctrlKey &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey
    ) {
      onKeyEnter(e)
      return
    }
    if (e.key === 'Escape') {
      onKeyEscape(e)
      return
    }
  }

  const onKeyEnter = (e: KeyboardEvent) => {
    if (!(e.target instanceof HTMLElement)) return
    e.preventDefault()

    if (!isSelected) {
      setIsSelected(true)
    }

    if (isEditing) return
    if (isReadOnly) return

    setIsEditing(true)
  }

  const onKeyEscape = (e: KeyboardEvent) => {
    if (!(e.target instanceof HTMLElement)) return
    e.preventDefault()

    if (isEditing) return

    e.target.blur()
    setIsSelected(false)
  }

  // #endregion

  return (
    <div
      key={cellKey}
      role="cell" // Semantic cell role (interpreted as 'gridcell') & structural ARIA hint for toolbar
      {
        // Type-safe HTML attributes
        ...({
          'data-cell-key': cellKey,
          'aria-selected': isSelected,
        } satisfies CellDomSchema)
      }
      className={frameStyle({
        isInPrivateNotesColumn: isInPrivateNotesColumn,
        isSelected: isSelected,
        isEditing: isEditing,
      })}
      style={{
        // Set CSS variables
        ['--col-width' as string]: `${col.width || COLUMN_DEFAULT_WIDTH}px`,
      }}
      tabIndex={-1}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
    >
      {children}

      <CellFrameHasRecentUpdate />
    </div>
  )
}

function CellFrameHasRecentUpdate() {
  const { cellKey, cell } = useCellState()
  const { editingCell } = useGridSnap()

  const cellUpdatedAt = cell?.updatedAt
  const initialUpdatedAt = useCached(cellUpdatedAt)
  const isRecentLocalUpdate = useCached(cellKey === editingCell)

  const [hasRecentUpdate, setHasRecentUpdate] = useState(false)

  useEffect(() => {
    if (!cellUpdatedAt || !initialUpdatedAt) return

    // Compare incoming timestamp to cached timestamp
    if (cellUpdatedAt <= initialUpdatedAt) return

    // Show content
    setHasRecentUpdate(true)
  }, [cellUpdatedAt, initialUpdatedAt])

  const handleAnimationEnd = useCallback(() => {
    // Unmount content when animation finishes
    setHasRecentUpdate(false)
  }, [])

  if (!hasRecentUpdate || isRecentLocalUpdate) return null

  return (
    <div
      className={cn(
        'pointer-events-none',
        hasRecentUpdate && !isRecentLocalUpdate && 'animate-fade-out',
      )}
      onAnimationEnd={handleAnimationEnd}
    >
      {/* Frame border */}
      <div className="absolute inset-0 rounded-sm ring-3 ring-yellow-500" />
      {/* Frame label */}
      <div className="absolute right-0 top-0 p-1 rounded-sm bg-yellow-500 text-xs text-black">
        New
      </div>
    </div>
  )
}
